import * as React from 'react'
import styled from 'styled-components'

import * as api from '@owl-nest/api-client/next'
import * as hooks from '@owl-nest/hooks'
import { t, tc } from '@owl-nest/localize'
import * as models from '@owl-nest/models'
import * as plume from '@ulule/owl-kit-components/next'
import * as services from '@owl-nest/services'
import * as duvet from '@ulule/duvet'

import { useDisplayMode } from '../hooks/useDisplayMode'
import { getCurrentLocale } from '../router'
import { NavigateBackLink } from '../components/NavigateBackLink'

import * as newsStyle from './style'

const PAGINATION_LIMIT = 10

export type NewsListProps = {
  newsList: api.NewsListPage
  project: models.project.Project
}

export function NewsList({ newsList: initialNewsList, project }: NewsListProps): React.ReactElement {
  const auth = services.user.useAuth()
  const displayMode = useDisplayMode(project)
  const isEditMode = displayMode === 'edit'
  const router = hooks.useNextRouter()

  const currentLocale = getCurrentLocale(router)
  const isAuthPending = auth.type === 'initial'
  const hasUserNewsReadPermission = project.permissions.news?.includes(api.ProjectNewsPermission.READ) ?? false

  // TODO: Make it a route param?
  const [selectedPage, setSelectedPage] = React.useState(1)
  const previouslySelectedPage = hooks.usePrevious(selectedPage)

  const useProjectNewsParams: services.news.UseProjectNewsParams = {
    limit: PAGINATION_LIMIT,
    page: selectedPage,
    sort: 'created_at',
  }

  if (hasUserNewsReadPermission) {
    useProjectNewsParams.status = 'online,waiting'
  } else {
    useProjectNewsParams.status = 'online'
  }

  const { response, query } = services.news.useProjectNews(project.id, {
    fetchOnMount: false,
    params: useProjectNewsParams,
    withToken: auth.type === 'loggedin',
  })

  React.useEffect(() => {
    if (!isAuthPending && response.status === hooks.QueryStatus.PRISTINE) {
      query()
    }
  }, [isAuthPending, response.status])

  React.useEffect(() => {
    if (previouslySelectedPage !== selectedPage && previouslySelectedPage !== undefined) {
      query()
    }
  }, [previouslySelectedPage, selectedPage])

  const metadata = response?.data?.meta && {
    pageCount: Math.floor((response.data.meta.total_count - 1) / response.data.meta.limit) + 1,
    selectedPage: selectedPage,
  }
  const newsList = selectedPage === 1 ? initialNewsList.news : response.data?.news

  const [activeVideo, setActiveVideo] = React.useState<number>()

  return (
    <Wrapper>
      <NavigateBackLink href={`/${project.slug}/`} label={t('Back to home page')} />
      <ListTitle as="h1" asDesktop="L">
        {tc('All [highlight: posts]', {
          highlight: <plume.HighlightedTitle />,
        })}
      </ListTitle>

      <React.Suspense fallback={<plume.Spinner />}>
        {newsList !== undefined && (
          <>
            <List>
              {newsList === null || newsList.length < 1 ? (
                <EmptyListContainer>
                  <plume.glyphs.stroke.Media size={49} />
                  <plume.styles.heading.XXXS>{t('No post for the moment')}</plume.styles.heading.XXXS>
                </EmptyListContainer>
              ) : (
                newsList.map((newsPiece) => {
                  const title = models.i18n.get(newsPiece.title)

                  return (
                    title && (
                      <duvet.NewsCard
                        href={`/${project.slug}/news/${newsPiece.slug}-${newsPiece.id}/`}
                        key={newsPiece.id}
                        isEditMode={isEditMode}
                        isVideoActive={activeVideo === newsPiece.id}
                        onVideoMount={() => {
                          setActiveVideo(newsPiece.id)
                        }}
                        locale={currentLocale}
                        news={newsPiece}
                        project={project}
                      />
                    )
                  )
                })
              )}
            </List>
            {metadata && Boolean(newsList?.length) && (
              <plume.Pagination
                onNavigate={(page) => {
                  if (typeof window !== 'undefined') {
                    window.scrollTo({ top: 0, behavior: 'smooth' })
                  }
                  setSelectedPage(page)
                }}
                pageCount={metadata?.pageCount}
                selectedPage={metadata?.selectedPage}
              />
            )}
          </>
        )}
      </React.Suspense>
    </Wrapper>
  )
}

const EmptyListContainer = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 30px;

  ${plume.styles.heading.XXXS} {
    color: ${plume.COLORS.PRIMARY_SAND_700};
  }

  ${plume.glyphs.Icon} {
    color: ${plume.COLORS.PRIMARY_SAND_500};
  }
`

const List = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-bottom: -8px;
`

const ListTitle = styled(plume.styles.heading.XS)`
  margin-bottom: 14px;

  @media screen and ${plume.BREAKPOINTS.TABLET} {
    margin: 8px auto 16px;
  }
`

const Wrapper = styled(newsStyle.Wrapper)`
  @media screen and (min-width: 536px) {
    max-width: 516px;
    margin-left: auto;
    margin-right: auto;
  }
`
